/**
 * Abertura do popup "Conecte a GIO na sua IA".
 *
 * Uma indicação só: ?connect_ai=1. Outro valor não abre.
 * Nas LPs essa mesma marca viaja com as UTMs (gio_utm_data). O popup abre
 * quando o tutorial da página acaba (ou é pulado). Sem tutorial, abre no
 * fim do onboarding. Não abre em todo onboarding.
 */

export const CONNECT_AI_QUERY = 'connect_ai';
export const CONNECT_AI_STORAGE_KEY = 'gio_connect_ai_popup';
export const CONNECT_AI_UTM_KEY = 'gio_utm_data';
export const CONNECT_AI_EVENT = 'gio-connect-ai-popup';

export const CONNECT_AI_REASONS = new Set(['1']);

export const CLAUDE_CONNECT_URL =
  'https://claude.ai/customize/connectors?modal=add-custom-connector&connectorName=GIO%20Brain&connectorUrl=https%3A%2F%2Fmcp.giobrain.com%2Fmcp';

export const GIO_MCP_URL = 'https://mcp.giobrain.com/mcp';

/**
 * Claude e ChatGPT. O clique na logo abre só o tutorial dessa IA, um passo por vez.
 * `image` fica null até a foto existir. O arquivo previsto mora em
 * frontend/public/connect-ai/ e entra como `/connect-ai/<imageFile>`.
 * `open` abre o link já preenchido. `copy-open` copia a URL do MCP e abre o site.
 */
export const CONNECT_AI_PLATFORMS = [
  {
    id: 'claude',
    labelKey: 'connectAi.claude',
    href: CLAUDE_CONNECT_URL,
    steps: [
      { textKey: 'connectAi.claudeStep1', image: null, imageFile: 'claude-1.png', action: 'open' },
      { textKey: 'connectAi.claudeStep2', image: null, imageFile: 'claude-2.png' },
    ],
  },
  {
    id: 'chatgpt',
    labelKey: 'connectAi.chatgpt',
    href: 'https://chatgpt.com/plugins',
    copyUrl: GIO_MCP_URL,
    steps: [
      {
        textKey: 'connectAi.chatgptStep1',
        images: ['/connect-ai/chatgpt-plugins.png', '/connect-ai/chatgpt-add.png'],
      },
      {
        textKey: 'connectAi.chatgptStep2',
        images: ['/connect-ai/chatgpt-form.png'],
        action: 'copy-open',
        showUrl: true,
      },
      {
        textKey: 'connectAi.chatgptStep3',
        images: ['/connect-ai/chatgpt-connect.png', '/connect-ai/chatgpt-login.png'],
      },
    ],
  },
];

function storageOr(storage) {
  if (storage) return storage;
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage;
}

export function connectAiReasonFromSearch(search) {
  const rawSearch = String(search || '');
  const params = new URLSearchParams(rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch);
  const reason = String(params.get(CONNECT_AI_QUERY) || '').trim().toLowerCase();
  return CONNECT_AI_REASONS.has(reason) ? reason : null;
}

export function rememberConnectAiReason(reason, storage) {
  const normalized = String(reason || '').trim().toLowerCase();
  if (!CONNECT_AI_REASONS.has(normalized)) return null;
  const store = storageOr(storage);
  if (store) store.setItem(CONNECT_AI_STORAGE_KEY, normalized);
  return normalized;
}

export function peekConnectAiReason(storage) {
  const store = storageOr(storage);
  if (!store) return null;
  const reason = String(store.getItem(CONNECT_AI_STORAGE_KEY) || '').trim().toLowerCase();
  return CONNECT_AI_REASONS.has(reason) ? reason : null;
}

export function forgetConnectAiReason(storage) {
  const store = storageOr(storage);
  if (store) store.removeItem(CONNECT_AI_STORAGE_KEY);
}

export function captureConnectAiFromSearch(search, storage) {
  const reason = connectAiReasonFromSearch(search);
  if (!reason) return null;
  return rememberConnectAiReason(reason, storage);
}

function utmStorageOr(storage) {
  if (storage) return storage;
  if (typeof localStorage === 'undefined') return null;
  return localStorage;
}

function readUtmBag(storage) {
  const store = utmStorageOr(storage);
  if (!store) return { store: null, bag: null };
  try {
    const raw = store.getItem(CONNECT_AI_UTM_KEY);
    const bag = raw ? JSON.parse(raw) : null;
    return { store, bag: bag && typeof bag === 'object' ? bag : null };
  } catch {
    return { store, bag: null };
  }
}

/** Guarda connect_ai=1 dentro das UTMs, sem abrir o popup. */
export function rememberConnectAiOnUtm(search, storage) {
  if (connectAiReasonFromSearch(search) !== '1') return false;
  const { store, bag } = readUtmBag(storage);
  if (!store) return false;
  const next = { ...(bag || {}), connect_ai: '1' };
  store.setItem(CONNECT_AI_UTM_KEY, JSON.stringify(next));
  return true;
}

/** Tira a marca das UTMs. true só se ela estava armada. */
export function consumeConnectAiFromUtm(storage) {
  const { store, bag } = readUtmBag(storage);
  if (!store || !bag || String(bag.connect_ai || '') !== '1') return false;
  delete bag.connect_ai;
  store.setItem(CONNECT_AI_UTM_KEY, JSON.stringify(bag));
  return true;
}

/** Fim de onboarding: abre só se a LP trouxe connect_ai=1 nas UTMs. */
export function maybeOpenConnectAiFromUtm(storage) {
  if (!consumeConnectAiFromUtm(storage)) return false;
  requestConnectAiPopup('1');
  return true;
}

export function requestConnectAiPopup(reason, storage) {
  const remembered = rememberConnectAiReason(reason, storage);
  if (!remembered || typeof window === 'undefined') return remembered;
  window.dispatchEvent(new CustomEvent(CONNECT_AI_EVENT, { detail: { reason: remembered } }));
  return remembered;
}

export function stripConnectAiSearch(search) {
  const rawSearch = String(search || '');
  const params = new URLSearchParams(rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch);
  if (!params.has(CONNECT_AI_QUERY)) return rawSearch;
  params.delete(CONNECT_AI_QUERY);
  const next = params.toString();
  return next ? `?${next}` : '';
}
